import React, { useState } from 'react';
import { useLanguage } from '../../hooks/useLanguage';

interface SaveDialogProps {
    isOpen: boolean;
    onSave: () => void;
    onDontSave: () => void;
    onCancel: () => void;
    onSaveAndVisitCommunity?: () => void;
    onSaveAndVisitItch?: () => void;
}

/**
 * Dialogue de sauvegarde avant fermeture du système - Style OS gouvernemental années 90
 * Interface authentique type terminal militaire avec esthétique high-tech de l'époque
 */
export const SaveDialog: React.FC<SaveDialogProps> = ({
    isOpen,
    onSave,
    onDontSave,
    onCancel,
    onSaveAndVisitCommunity,
    onSaveAndVisitItch
}) => {
    const { t } = useLanguage();
    const [dontShowAgain, setDontShowAgain] = useState(false);
    
    if (!isOpen) return null;

    const handleSave = () => {
        if (dontShowAgain) {
            localStorage.setItem('ALBION_SKIP_SHUTDOWN_CONFIRM', 'true');
        }
        onSave();
    };

    const handleSaveAndVisitCommunity = () => {
        if (dontShowAgain) {
            localStorage.setItem('ALBION_SKIP_SHUTDOWN_CONFIRM', 'true');
        }
        if (onSaveAndVisitCommunity) {
            onSaveAndVisitCommunity();
        }
    };

    const handleSaveAndVisitItch = () => {
        if (dontShowAgain) {
            localStorage.setItem('ALBION_SKIP_SHUTDOWN_CONFIRM', 'true');
        }
        if (onSaveAndVisitItch) {
            onSaveAndVisitItch();
        }
    };

    return (
        <>
            {/* Overlay modal avec effet CRT */}
            <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center backdrop-blur-sm">
                {/* Fenêtre de dialogue style terminal gouvernemental */}
                <div className="bg-slate-900 border-4 border-slate-600 shadow-2xl w-[480px] font-mono relative">
                    {/* Effet de bordure interne style CRT */}
                    <div className="absolute inset-1 border border-slate-700 pointer-events-none"></div>
                    
                    {/* Barre de titre style OS militaire */}
                    <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 px-4 py-3 border-b-2 border-slate-600 flex items-center gap-3">
                        {/* Indicateur de statut système */}
                        <div className="w-3 h-3 bg-cyan-500 rounded-sm animate-pulse shadow-[0_0_6px_rgba(34,211,238,0.8)]"></div>
                        <span className="text-cyan-400 text-sm font-bold uppercase tracking-[0.2em] flex-1">
                            {t('saveDialog.shutdownProcedure')}
                        </span>
                        {/* Code de session */}
                        <span className="text-slate-500 text-xs">{t('saveDialog.sessionCode')}-{Date.now().toString().slice(-6)}</span>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-6 bg-slate-900">
                        {/* Message de sauvegarde automatique */}
                        <div className="border-l-4 border-cyan-500 pl-4 mb-6 bg-cyan-500/5">
                            <div className="text-cyan-400 text-xs font-bold uppercase tracking-wider mb-1">
                                ✓ {t('saveDialog.autoSaveComplete')}
                            </div>
                            <div className="text-slate-300 text-xs">
                                {t('saveDialog.allDataSaved')}
                            </div>
                        </div>

                        {/* Message d'accès temporaire terminé */}
                        <div className="border-l-4 border-amber-500 pl-4 mb-6 bg-amber-500/5">
                            <div className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-1">
                                ⚠ {t('saveDialog.temporaryAccessEnded')}
                            </div>
                            <div className="text-slate-300 text-xs">
                                {t('saveDialog.supportProject')}
                            </div>
                        </div>

                        {/* Bouton Itch.io */}
                        <div className="mb-6">
                            {onSaveAndVisitItch && (
                                <button
                                    onClick={handleSaveAndVisitItch}
                                    className="w-full px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white border-2 border-red-500 hover:from-red-500 hover:to-red-600 hover:border-red-400 transition-all text-sm font-bold uppercase tracking-wider shadow-xl shadow-red-500/30 relative overflow-hidden group"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                    <div className="relative flex items-center justify-center gap-3">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 245.371 220.736">
                                            <path d="M31.99 1.365C21.287 7.72.2 18.437.2 18.437l-.169.156c-.137.134-.169.268-.169.367v.15c0 .134.033.268.134.401l.135.135 62.956 106.924c.134.134.268.201.435.201.134 0 .268-.067.402-.201l.134-.135 62.956-106.924.135-.135c.1-.133.134-.267.134-.401v-.15c0-.099-.032-.233-.169-.367l-.169-.156S125.891 7.72 115.188 1.365C104.485-4.99 94.918 1.365 94.918 1.365S85.35-4.99 74.647 1.365C63.944 7.72 42.857 18.437 42.857 18.437l-.169.156c-.137.134-.169.268-.169.367v.15c0 .134.033.268.134.401l.135.135 62.956 106.924c.134.134.268.201.435.201.134 0 .268-.067.402-.201l.134-.135 62.956-106.924.135-.135c.1-.133.134-.267.134-.401v-.15c0-.099-.032-.233-.169-.367l-.169-.156S208.48 7.72 197.777 1.365C187.074-4.99 177.507 1.365 177.507 1.365s-9.568-6.355-20.271 0z"/>
                                        </svg>
                                        <span>{t('saveDialog.supportOnItch')}</span>
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* Séparateur */}
                        <div className="relative mb-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-700"></div>
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-slate-900 px-2 text-slate-500">{t('saveDialog.or')}</span>
                            </div>
                        </div>

                        {/* Appel à l'action communauté */}
                        <div className="mb-6 text-center">
                            <div className="text-slate-100 font-bold mb-3 text-base uppercase tracking-wide">
                                {t('saveDialog.joinCommunity')}
                            </div>
                            <div className="text-slate-300 text-sm leading-relaxed space-y-2">
                                <div>{t('saveDialog.communityMessage1')}</div>
                                <div className="text-cyan-400">{t('saveDialog.communityMessage2')}</div>
                            </div>
                        </div>

                        {/* Icône Facebook stylisée */}
                        <div className="flex justify-center mb-6">
                            <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-cyan-800 rounded-lg flex items-center justify-center border-2 border-cyan-500 shadow-lg shadow-cyan-500/30">
                                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                            </div>
                        </div>

                        {/* Case à cocher "Ne plus afficher" */}
                        <div className="mb-4 flex items-center gap-2 px-2">
                            <input
                                type="checkbox"
                                id="dontShowAgainSave"
                                checked={dontShowAgain}
                                onChange={(e) => setDontShowAgain(e.target.checked)}
                                className="w-4 h-4 bg-slate-700 border-2 border-slate-500 rounded cursor-pointer accent-cyan-500"
                            />
                            <label 
                                htmlFor="dontShowAgainSave" 
                                className="text-xs text-slate-400 cursor-pointer select-none hover:text-slate-300 transition-colors"
                            >
                                {t('saveDialog.dontShowAgain')}
                            </label>
                        </div>

                        {/* Boutons d'action */}
                        <div className="flex flex-col gap-3">
                            {/* Bouton principal : Rejoindre la communauté */}
                            {onSaveAndVisitCommunity && (
                                <button
                                    onClick={handleSaveAndVisitCommunity}
                                    className="w-full px-6 py-4 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white border-2 border-cyan-500 hover:from-cyan-500 hover:to-cyan-600 hover:border-cyan-400 transition-all text-sm font-bold uppercase tracking-wider shadow-xl shadow-cyan-500/30 relative overflow-hidden group"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                    <div className="relative flex items-center justify-center gap-3">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                        </svg>
                                        <span>{t('saveDialog.joinAndQuit')}</span>
                                    </div>
                                </button>
                            )}
                            
                            {/* Boutons secondaires */}
                            <div className="flex gap-3">
                                <button
                                    onClick={onCancel}
                                    className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 border-2 border-slate-600 hover:bg-slate-600 hover:text-cyan-400 hover:border-cyan-600 transition-all text-xs font-bold uppercase tracking-wider"
                                >
                                    {t('saveDialog.cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="flex-1 px-4 py-2 bg-slate-800 text-slate-300 border-2 border-slate-700 hover:bg-slate-700 hover:text-slate-100 hover:border-slate-600 transition-all text-xs font-bold uppercase tracking-wider"
                                >
                                    {t('saveDialog.quitWithoutVisit')}
                                </button>
                            </div>
                        </div>

                        {/* Footer de sécurité */}
                        <div className="mt-4 pt-3 border-t border-slate-700 text-slate-500 text-[10px] uppercase tracking-wider text-center">
                            {t('saveDialog.securedSystem')}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};